/**
 * Workspace picker (design 3b) — the step after Clerk sign-in for operators
 * in more than one org. One workspace → straight through to the dashboard,
 * no stop. Themed branded card; theme-aware via Radix tokens.
 *
 * Clerk owns membership: useOrganizationList gives the user's orgs, setActive
 * selects one, then /clerk/me runs the JIT org/user/tenant sync.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrganizationList, CreateOrganization } from '@clerk/react';
import api from '../../utils/api';
import { useSiteConfig } from '../../utils/siteConfig';
import './Workspace.css';

export default function Workspace() {
  const navigate = useNavigate();
  // Product brand on the operator domain, venue org brand elsewhere.
  const { get } = useSiteConfig();
  const brandColor = get('brandColor', '#840032');
  const brandName = get('brandName');

  const { isLoaded, userMemberships, setActive } = useOrganizationList({
    userMemberships: { infinite: true },
  });

  const [selecting, setSelecting] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState('');
  const autoTried = useRef(false);

  // Activate an org, run the backend sync, then land on the dashboard.
  const choose = useCallback(async (orgId: string) => {
    if (!setActive) return;
    setSelecting(orgId);
    setError('');
    try {
      await setActive({ organization: orgId });
      await api.get('/api/clerk/me');
      navigate('/dashboard');
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setError(err.response?.data?.message || err.message || 'Could not open that workspace');
      setSelecting(null);
    }
  }, [setActive, navigate]);

  const memberships = useMemo(() => userMemberships?.data ?? [], [userMemberships?.data]);

  // Exactly one workspace → skip the picker entirely.
  useEffect(() => {
    if (!isLoaded || autoTried.current) return;
    if (memberships.length === 1) {
      autoTried.current = true;
      choose(memberships[0]!.organization.id);
    }
  }, [isLoaded, memberships, choose]);

  return (
    <div className="ws-page">
      <div className="ws-card">
        <span className="ws-wordmark" style={{ color: brandColor }}>{brandName}</span>
        <div className="ws-head">
          <div className="ws-title">Choose a workspace</div>
          <div className="ws-subtitle">Quizzes, venues and game nights are separate per workspace.</div>
        </div>

        {error && <div className="ws-error">{error}</div>}

        {!isLoaded ? (
          <div className="ws-loading">Loading your workspaces…</div>
        ) : memberships.length === 0 && !showCreate ? (
          <div className="ws-empty">You’re not in a workspace yet.</div>
        ) : (
          memberships.map((m, i) => {
            const org = m.organization;
            const tileInitial = (org.name.trim()[0] || 'W').toUpperCase();
            return (
              <button
                key={org.id}
                type="button"
                className={`ws-row ${i === 0 ? 'primary' : ''}`}
                onClick={() => choose(org.id)}
                disabled={selecting != null}
              >
                {org.imageUrl ? (
                  <img className="ws-tile" src={org.imageUrl} alt="" />
                ) : (
                  <span className="ws-tile" style={{ background: i === 0 ? brandColor : 'var(--gray-12)' }}>{tileInitial}</span>
                )}
                <span className="ws-row-id">
                  <span className="ws-row-name">{org.name}</span>
                  <span className="ws-row-meta">{m.role?.replace('org:', '') || 'Member'}</span>
                </span>
                <span className="ws-row-arrow">{selecting === org.id ? '…' : '→'}</span>
              </button>
            );
          })
        )}

        {showCreate ? (
          <CreateOrganization afterCreateOrganizationUrl="/dashboard" skipInvitationScreen />
        ) : (
          <button type="button" className="ws-create" onClick={() => setShowCreate(true)}>
            + Create a new workspace
          </button>
        )}
      </div>
    </div>
  );
}
